import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCashiers } from '@/hooks/useCashiers';
import { useOutlets } from '@/hooks/useOutlets';
import { api } from '@/lib/api';

export function CashiersPage() {
  const navigate = useNavigate();
  const [selectedOutletFilter, setSelectedOutletFilter] = useState<string>('');
  const { cashiers, isLoading, error, refetch } = useCashiers(selectedOutletFilter || undefined);
  const { outlets } = useOutlets();
  const [searchTerm, setSearchTerm] = useState('');

  const handleDelete = async (userId: string, username: string) => {
    if (!confirm(`Are you sure you want to delete cashier "${username}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await api.cashiers.delete(userId);
      if (response.success) {
        refetch();
      } else {
        alert(response.error?.message || 'Failed to delete cashier');
      }
    } catch (err) {
      alert('Failed to delete cashier');
    }
  };

  const getStatusBadgeColor = (isActive: boolean) => {
    return isActive
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getOutletNames = (outletIds: string[]) => {
    if (outletIds.length === 0) return 'None';
    const names = outletIds
      .map(id => outlets.find(o => o.outletId === id)?.name)
      .filter(Boolean);
    if (names.length === 0) return 'Unknown';
    if (names.length === 1) return names[0];
    return `${names[0]} +${names.length - 1}`;
  };

  // Apply client-side search filter by username, full name, or email
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredCashiers = normalizedSearchTerm
    ? cashiers.filter((cashier) => {
        const username = cashier.username?.toLowerCase() ?? '';
        const fullName = cashier.fullName?.toLowerCase() ?? '';
        const email = cashier.email?.toLowerCase() ?? '';
        return (
          username.includes(normalizedSearchTerm) ||
          fullName.includes(normalizedSearchTerm) ||
          email.includes(normalizedSearchTerm)
        );
      })
    : cashiers;

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-text">Cashiers</h1>
          <p className="text-text-muted mt-1">
            Manage cashier accounts and outlet access
          </p>
        </div>
        <button
          onClick={() => navigate('/cashiers/new')}
          className="bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          + Create Cashier
        </button>
      </div>

      {/* Filter by outlet */}
      <div className="mb-6">
        <label htmlFor="outlet-filter" className="block text-sm font-medium text-text mb-2">
          Filter by Outlet
        </label>
        <select
          id="outlet-filter"
          value={selectedOutletFilter}
          onChange={(e) => setSelectedOutletFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-64"
        >
          <option value="">All Outlets</option>
          {outlets.map((outlet) => (
            <option key={outlet.outletId} value={outlet.outletId}>
              {outlet.name}
            </option>
          ))}
        </select>
      </div>

      {/* Search by username, full name, or email */}
      <div className="mb-6">
        <label htmlFor="cashier-search" className="block text-sm font-medium text-text mb-2">
          Search Cashiers
        </label>
        <input
          id="cashier-search"
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-full max-w-md"
          placeholder="Search by username, name, or email"
        />
      </div>

      {filteredCashiers.length === 0 ? (
        <div className="bg-surface rounded-lg shadow p-12 text-center">
          <p className="text-text-muted mb-4">
            {normalizedSearchTerm
              ? 'No cashiers match your search'
              : selectedOutletFilter
              ? 'No cashiers found for this outlet'
              : 'No cashiers found'}
          </p>
          <button
            onClick={() => navigate('/cashiers/new')}
            className="bg-primary text-white px-6 py-2 rounded-md hover:bg-blue-700"
          >
            Create Your First Cashier
          </button>
        </div>
      ) : (
        <div className="bg-surface rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Username
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Full Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Outlets
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCashiers.map((cashier) => (
                <tr key={cashier.userId}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-text">{cashier.username}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-text">{cashier.fullName || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-text-muted">{cashier.email || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-text-muted">
                      {getOutletNames(cashier.outletIds)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusBadgeColor(
                        cashier.isActive
                      )}`}
                    >
                      {cashier.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => navigate(`/cashiers/${cashier.userId}/edit`)}
                        className="text-primary hover:text-blue-700"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(cashier.userId, cashier.username)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedOutletFilter && filteredCashiers.length > 0 && (
        <p className="text-sm text-text-muted mt-4">
          Showing {filteredCashiers.length} cashier{filteredCashiers.length !== 1 ? 's' : ''} for selected outlet
        </p>
      )}
    </div>
  );
}
